/**
 * Where the risk is concentrated: impact against probability, five by five.
 *
 * A risk register sorted by score answers "what is worst". This answers a different and
 * harder question — *what shape* is the exposure. Twelve risks clustered at high-probability,
 * low-impact is a delivery problem; three at the top right is a board problem. A list cannot
 * show that and a grid can, in about two seconds.
 *
 * The cell colour follows the score, on the client's own thresholds: 16 and above must be
 * reported upward, 9 and above is watched. Every cell also carries its count as a number, so
 * the grid is readable when the colours are not — printed, projected, or by someone who
 * cannot tell the two reds apart.
 */
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { api } from '@/api/client'

type Cell = { impact: number; probability: number; count: number }

const SCALE = [1, 2, 3, 4, 5] as const

export function RiskMatrix({ packageId }: { packageId: string }) {
  const { t } = useTranslation()

  const cells = useQuery({
    queryKey: ['package', packageId, 'risk-matrix'],
    queryFn: ({ signal }) => api.get<Cell[]>(`/packages/${packageId}/risk-matrix`, signal),
  })

  const counts = new Map<string, number>()
  for (const cell of cells.data ?? []) {
    counts.set(`${cell.impact}:${cell.probability}`, cell.count)
  }
  const total = (cells.data ?? []).reduce((sum, cell) => sum + cell.count, 0)

  // Nothing scored yet. A five-by-five grid of zeroes tells nobody anything and takes the
  // width the register itself should have.
  if (!cells.data || total === 0) return null

  return (
    <section className="card card--pad matrix" data-testid="risk-matrix">
      <header className="matrix__head">
        <h2 className="card__title">{t('matrix.title')}</h2>
        <p className="card__caption">{t('matrix.subtitle')}</p>
      </header>

      <div className="matrix__grid" role="img" aria-label={t('matrix.aria', { count: total })}>
        {/* Impact descends down the rows, so the worst corner is top-right — the way a risk
            matrix is drawn in every methodology the client already uses. */}
        {[...SCALE].reverse().map((impact) => (
          <div className="matrix__row" key={impact}>
            <span className="matrix__axis" aria-hidden="true">
              {impact}
            </span>
            {SCALE.map((probability) => {
              const count = counts.get(`${impact}:${probability}`) ?? 0
              const score = impact * probability
              return (
                <span
                  key={probability}
                  className="matrix__cell"
                  data-band={score >= 16 ? 'bad' : score >= 9 ? 'watch' : 'good'}
                  data-empty={count === 0 ? 'true' : undefined}
                  title={t('matrix.cell', { impact, probability, count, score })}
                >
                  {count > 0 ? count : ''}
                </span>
              )
            })}
          </div>
        ))}
        <div className="matrix__row">
          <span className="matrix__axis" aria-hidden="true" />
          {SCALE.map((probability) => (
            <span className="matrix__axis" key={probability} aria-hidden="true">
              {probability}
            </span>
          ))}
        </div>
      </div>

      <p className="matrix__legend">
        <span>{t('matrix.impact')}</span>
        <span>{t('matrix.probability')}</span>
      </p>
    </section>
  )
}